import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {AxiosError} from "axios";
import {Bindings} from "data/constants/bindings";
import type {IRankingsApiProvider} from "data/providers/api/rankings.api.provider";
import type {IRankingsRow, IRankingsTableObject} from "data/types/entities";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IAxiosApiError, IGameBarResponse} from "data/types/api";
import type {NullOrEmpty} from "data/types/generics";
import type {ILeaderboardFilters} from "views/pages/leaderboard/leaderboard.controller";
import {DirectionFilter, RankingsFiltersType} from "data/enums";

export enum LeaderboardType {
	Season = "",
	Playoff = "/playoff",
}

export interface IRankingsStore {
	get filters(): ILeaderboardFilters;
	get isLoading(): boolean;
	get overallTable(): IRankingsTableObject;
	get nextPage(): boolean;
	get gameBarData(): IGameBarResponse;
	get selectRound(): number | undefined;
	get type(): LeaderboardType;

	changeFilters: (type: RankingsFiltersType) => void;
	fetchOverallLeaderboard: () => Promise<void>;
	fetchMoreLeaderboards: () => Promise<void>;
	fetchGameBar: (roundId: number | null) => Promise<void>;
	setContestId: (roundId: number | undefined) => void;
	setType: (type: LeaderboardType) => void;
}

const defaultGameBarData: IGameBarResponse = {
	overallPoints: null,
	overallRank: null,
	roundPoints: null,
	roundRank: null,
};

@injectable()
export class RankingsStore implements IRankingsStore {
	@observable private _gameBarData: IGameBarResponse = defaultGameBarData;
	@observable private _isLoading: boolean = false;
	@observable private _selectedRound: number | undefined = undefined;
	@observable private _type = LeaderboardType.Season;
	@observable private _list: IRankingsRow[] = [];
	@observable private _user: NullOrEmpty<IRankingsRow> = null;
	@observable private _nextPage: boolean = false;
	@observable private _filters: ILeaderboardFilters = {
		direction: DirectionFilter.Low,
		type: RankingsFiltersType.Rank,
	};
	private _page: number = 0;
	private _limit: number = 20;
	private columnsDefaultSort = {
		[RankingsFiltersType.Rank]: DirectionFilter.Low,
		[RankingsFiltersType.Points]: DirectionFilter.High,
		[RankingsFiltersType.Margin]: DirectionFilter.High,
	};

	get type() {
		return this._type;
	}

	get isLoading(): boolean {
		return this._isLoading;
	}

	get nextPage(): boolean {
		return this._nextPage;
	}

	get overallTable(): IRankingsTableObject {
		return {
			rankings: this._list,
			user: this._user,
		};
	}

	get gameBarData() {
		return this._gameBarData;
	}

	get filters() {
		return this._filters;
	}

	get selectRound() {
		return this._selectedRound;
	}

	constructor(
		@inject(Bindings.RankingsApiProvider) private _rankingsApiProvider: IRankingsApiProvider,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	async fetchMoreLeaderboards() {
		this._page += 1;

		const {
			data: {success},
		} = await this._rankingsApiProvider.fetchOverallRankings({
			type: this._type,
			page: this._page,
			limit: this._limit,
			startId: this._selectedRound || undefined,
			orderBy: this._filters.type,
			orderDir: this._filters.direction,
		});

		runInAction(() => {
			this._list = [...this._list, ...success.rankings];
			this._nextPage = success.nextPage;
		});
	}

	@action
	public async fetchOverallLeaderboard(): Promise<void> {
		this.clearStore();

		const {
			data: {success},
		} = await this._rankingsApiProvider.fetchOverallRankings({
			type: this._type,
			limit: this._limit,
			startId: this._selectedRound || undefined,
			orderBy: this._filters.type,
			orderDir: this._filters.direction,
		});

		runInAction(() => {
			this._list = success.rankings;
			this._user = success.user;
			this._nextPage = success.nextPage;
			this._page = 1;
		});
	}

	@action
	changeFilters = (type: RankingsFiltersType) => {
		if (type === this._filters.type) {
			this._filters = {
				...this._filters,
				direction:
					this._filters.direction === DirectionFilter.Low
						? DirectionFilter.High
						: DirectionFilter.Low,
			};
		} else {
			this._filters = {
				type,
				direction: this.columnsDefaultSort[type],
			};
		}
	};

	@action setContestId(roundId: number | undefined) {
		this._selectedRound = roundId;
	}

	@action
	public async fetchGameBar(roundId: number | null): Promise<void> {
		try {
			this._isLoading = true;
			const response = await this._rankingsApiProvider.fetchGameBar(roundId);
			this._gameBarData = response.data.success;
			return Promise.resolve();
		} catch (e) {
			this.onError(e);
			return Promise.reject(e);
		} finally {
			this._isLoading = false;
		}
	}

	@action
	setType(type: LeaderboardType) {
		this._type = type;
		this._selectedRound = undefined;
	}

	protected onError(e: unknown) {
		const error = e as AxiosError<IAxiosApiError, unknown>;
		this._modalsStore.showAxiosError(error);
	}

	clearStore = () => {
		this._list = [];
		this._user = null;
		this._nextPage = false;
		this._page = 0;
	};
}
